import React from 'react';
import {
  Badge,
  type BadgeColor,
  type BadgeVariant,
  Flex,
  HoverCardRoot,
  HoverCardTrigger,
  HoverCardContent,
  Skeleton,
  Text,
} from '@/components/ui';

interface ChipsCellProps {
  value?: unknown;
  maxVisible?: number;
  chipSize?: 'xsmall' | 'small' | 'medium';
  chipColor?: BadgeColor;
  chipVariant?: BadgeVariant;
  render?: () => React.ReactNode;
}

function ChipsCell({
  value,
  maxVisible = 2,
  chipSize = 'xsmall',
  chipColor = 'default',
  chipVariant = 'surface',
  render,
}: ChipsCellProps) {
  if (render) {
    return <>{render()}</>;
  }

  // Handle array of strings
  let chips: string[] = [];
  if (Array.isArray(value)) {
    chips = value.filter((item): item is string => typeof item === 'string');
  } else if (typeof value === 'string') {
    chips = [value];
  }

  if (chips.length === 0) {
    return (
      <Text size="2" color="gray" className="data-cell__chips">
        —
      </Text>
    );
  }

  const visibleChips = chips.slice(0, maxVisible);
  const remainingCount = chips.length - maxVisible;

  return (
    <Flex gap="1" wrap="wrap" align="center" className="data-cell__chips">
      {visibleChips.map((chip, index) => (
        <Badge
          key={index}
          size={chipSize}
          color={chipColor}
          variant={chipVariant}
          highContrast
        >
          {chip}
        </Badge>
      ))}
      {remainingCount > 0 && (
        <HoverCardRoot>
          <HoverCardTrigger>
            <Badge
              size={chipSize}
              color={chipColor}
              variant={chipVariant}
              style={{ cursor: 'pointer' }}
            >
              +{remainingCount}
            </Badge>
          </HoverCardTrigger>
          <HoverCardContent size="2">
            <Flex direction="column" gap="2">
              <Text size="2" weight="medium">
                All items ({chips.length})
              </Text>
              <Flex gap="1" wrap="wrap">
                {chips.map((chip, index) => (
                  <Badge
                    key={index}
                    size={chipSize}
                    color={chipColor}
                    variant={chipVariant}
                    highContrast
                  >
                    {chip}
                  </Badge>
                ))}
              </Flex>
            </Flex>
          </HoverCardContent>
        </HoverCardRoot>
      )}
    </Flex>
  );
}

export function ChipsCellSkeleton() {
  return (
    <Flex gap="1" wrap="wrap">
      <Skeleton style={{ width: '60px', height: '20px', borderRadius: '4px' }} />
      <Skeleton style={{ width: '60px', height: '20px', borderRadius: '4px' }} />
      <Skeleton style={{ width: '40px', height: '20px', borderRadius: '4px' }} />
    </Flex>
  );
}

export default ChipsCell;

